import {
  BiCardView,
  BiDashboardFilters,
  BiDashboardView,
} from '@euler/model/bi/dashboard';
import { BiQueryResult } from '@euler/model/bi/result';
import { kBiDefaultPalette } from '@euler/model/bi/viz';
import { ReactNode, useCallback } from 'react';
import { BiDashboardCard } from '../card/BiDashboardCard';
import { BiCardMove } from './biCardMove';
import { BiCardResults } from './biCardResults';

/**
 * How the grid draws a card: with its page link and, for editors, edit /
 * remove in its menu, plus the drag grip where it can be dragged and the
 * menu's moves where it cannot; or plain, as a preview draws the unchanged.
 */
export function useBiCardRenderers({
  dashboard,
  filters,
  refreshTick,
  canEdit,
  order,
  results,
  onOpen,
  onEdit,
  onMove,
  onRemove,
}: {
  dashboard: BiDashboardView | null;
  /** The viewer's own date range; undefined applies the saved one. */
  filters: BiDashboardFilters | undefined;
  /** Bumped when the viewer refreshes the dashboard. */
  refreshTick: number;
  canEdit: boolean;
  /** The cards in reading order, which the moves step through. */
  order: string[];
  /** Where each card reports the data it loaded. */
  results: BiCardResults;
  onOpen: (cardId: string) => void;
  onEdit: (card: BiCardView, loaded: BiQueryResult | null) => void;
  onMove: (card: BiCardView, move: BiCardMove) => void;
  onRemove: (card: BiCardView) => void;
}) {
  const savedFilters = dashboard?.filters;
  const palette = dashboard?.palette ?? kBiDefaultPalette;
  const renderPlainCard = useCallback(
    (
      card: BiCardView,
      height: number | undefined,
      _grip?: ReactNode,
      _footer?: ReactNode,
      onFitHeight?: (px: number) => void,
    ) =>
      savedFilters ? (
        <BiDashboardCard
          card={card}
          height={height}
          filters={filters}
          savedFilters={savedFilters}
          refreshTick={refreshTick}
          palette={palette}
          onFitHeight={onFitHeight}
        />
      ) : null,
    [filters, savedFilters, refreshTick, palette],
  );
  const renderCard = useCallback(
    (
      card: BiCardView,
      height: number | undefined,
      grip?: ReactNode,
      footer?: ReactNode,
      onFitHeight?: (px: number) => void,
    ) => {
      if (!savedFilters) return null;
      const index = order.indexOf(card.id);
      return (
        <BiDashboardCard
          card={card}
          height={height}
          filters={filters}
          savedFilters={savedFilters}
          refreshTick={refreshTick}
          palette={palette}
          grip={canEdit ? grip : undefined}
          footer={canEdit ? footer : undefined}
          onOpen={() => onOpen(card.id)}
          onEdit={canEdit ? loaded => onEdit(card, loaded) : undefined}
          moves={
            canEdit && !grip && order.length > 1 && index >= 0
              ? {
                  first: index === 0,
                  last: index === order.length - 1,
                  onMove: move => onMove(card, move),
                }
              : undefined
          }
          onRemove={canEdit ? () => onRemove(card) : undefined}
          onFitHeight={onFitHeight}
          onData={results.report}
        />
      );
    },
    [
      filters,
      refreshTick,
      savedFilters,
      palette,
      canEdit,
      order,
      results,
      onOpen,
      onEdit,
      onMove,
      onRemove,
    ],
  );
  return { renderCard, renderPlainCard };
}
